/**
 * Chat state and the request/stream cycle for the assistant panel: sending a
 * message (with page context, attachments, a confirmation) by the
 * `sendAgentMessage` mutation, running the turn by subscribing to
 * `agentMessageStream(turnId)`, executing a returned `navigate` action, and
 * reopening a stored conversation via `agentConversation(id)` (MEIS-13516,
 * P1 — replaces `POST`/`GET /api/agent/chat`/`conversations`). State resets
 * when `resetKey` changes (design D8: the selected user or organisation
 * changed) — in the very render in which it changes, so a chat box
 * remounted for the new identity never sees the previous one's messages.
 *
 * Every turn is kept in `backgroundTurns` (`agentBackgroundTurns.ts`): a new
 * chat or another conversation only detaches the running turn, which keeps
 * going and is shown live again when its conversation is reopened. The
 * reducer below always holds the *shown* conversation; an identity switch
 * clears the background turns too.
 */
import { ApolloError, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import * as Sentry from "@sentry/react";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { UploadedAgentAttachment } from "./AgentAttachments.js";
import {
  AgentBackgroundTurns,
  provisionalConversationTitle,
} from "./agentBackgroundTurns.js";
import {
  AgentStreamAbortedError,
  AgentStreamError,
  readAgentMessageStream,
  type AgentChatResult,
  type AgentStreamEvent,
  type AgentTierBreakdown,
  type AgentToolCall,
  type AgentUiAction,
} from "./agentStream.js";
import type { PageContext } from "./pageContext.js";
import { useRefreshAfterAgentWrite } from "./refreshAfterAgentWrite.js";
import {
  AgentConversationHistoryDocument,
  AgentMessageStreamDocument,
  SendAgentMessageDocument,
  StopAgentTurnDocument,
  type AgentConversationHistoryQuery,
  type AgentConversationHistoryQueryVariables,
  type AgentMessageStreamSubscription,
  type AgentMessageStreamSubscriptionVariables,
  type SendAgentMessageMutation,
  type SendAgentMessageMutationVariables,
  type StopAgentTurnMutation,
  type StopAgentTurnMutationVariables,
} from "./useAgentChat.generated.js";

export type AgentChatMessage = {
  /** Stored answer id (assistant messages only, once persisted). */
  id?: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: AgentToolCall[];
  uiActions?: AgentUiAction[];
  attachmentNames?: string[];
  /** Assistant messages only, once known — for the per-answer cost line (`agentChatAdapter.js`). */
  model?: string | null;
  costUsd?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  tiers?: AgentTierBreakdown;
  escalatedAtIteration?: number | null;
};

export type AgentToolProgress = {
  /** The model's tool-call id — what a `tool_done` is matched to its start by. */
  callId: string;
  name: string;
  done: boolean;
  failed: boolean;
};

export type AgentChatState = {
  messages: AgentChatMessage[];
  conversationId: string | null;
  isLoading: boolean;
  progress: AgentToolProgress[];
  error: string | null;
  /**
   * The answer as the model writes it, before it is stored (`text_delta`);
   * cleared by a `text_discard` or once the turn ends (`result`/`error`).
   * Not one of `messages` — a live draft only.
   */
  draftText: string;
  /**
   * The backend refused the last send because a turn is still running in
   * this conversation (`AGENT_CONFLICT`). Shown as an inline notice, not as
   * an error; cleared by the next send, a new chat or another conversation.
   */
  conflict: boolean;
  /** The user asked to stop the shown turn (`stop`); it still ends through its result. */
  stopping: boolean;
};

export const initialAgentChatState: AgentChatState = {
  messages: [],
  conversationId: null,
  isLoading: false,
  progress: [],
  error: null,
  draftText: "",
  conflict: false,
  stopping: false,
};

/** `extensions.code` of the backend's refusal while a turn still runs in the same conversation. */
export const AGENT_CONFLICT_CODE = "AGENT_CONFLICT";

/**
 * The `reason` of the terminal `AgentTurnUnavailableEvent` a raced send's
 * stream gets when an earlier turn of the same conversation won the lock
 * (`agentMessageStream.ts`) — the same text `sendAgentMessage`'s
 * `AGENT_CONFLICT` refusal carries. Matched exactly: the event has no code.
 */
export const AGENT_TURN_BUSY_REASON =
  "The assistant is still working on your last message.";

/**
 * The `reason` of the terminal `AgentTurnUnavailableEvent` a stream gets when
 * its turn was already taken by an earlier subscribe (`agentMessageStream.ts`)
 * — what graphql-sse's re-subscribe after a dropped connection runs into
 * while the server goes on with the turn. Matched exactly: the event has no
 * code, and the sibling "conversation was deleted" reason must stay a failure.
 */
export const AGENT_TURN_TAKEN_REASON =
  "This turn is no longer available — it was already run or delivered.";

/** How often the stored conversation is polled for the answer of a turn whose stream was lost. */
export const AGENT_RECOVERY_POLL_MS = 3000;

/** Mirrors the backend's `MAX_AGENT_CHAT_TURN_MS`: past it no turn can still be running. */
export const AGENT_RECOVERY_MAX_MS = 30 * 60 * 1000;

/**
 * Whether `err` is the backend's "a turn is still running in this
 * conversation" refusal — the mutation's `AGENT_CONFLICT`, or the stream's
 * `AgentTurnUnavailableEvent` with `AGENT_TURN_BUSY_REASON` when two sends
 * raced past the mutation's fail-fast check.
 */
export function isAgentConflictError(err: unknown): boolean {
  if (err instanceof AgentStreamError)
    return err.message === AGENT_TURN_BUSY_REASON;
  return (
    err instanceof ApolloError &&
    err.graphQLErrors.some(e => e.extensions?.["code"] === AGENT_CONFLICT_CODE)
  );
}

/**
 * How a `send` ended: `"conflict"` when the backend refused it because a
 * turn is still running (nothing was added to the conversation — the caller
 * keeps the typed text), `"done"` otherwise (answered, failed or discarded).
 */
export type AgentSendOutcome = "done" | "conflict";

export type AgentChatAction =
  | { type: "sendStart"; userMessage: AgentChatMessage }
  | { type: "conversation"; conversationId: string }
  | { type: "toolStart"; callId: string; name: string; input: unknown }
  | { type: "toolDone"; callId: string; entry: AgentToolCall }
  | { type: "textDelta"; delta: string }
  | { type: "textDiscard" }
  | { type: "result"; assistantMessage: AgentChatMessage }
  | { type: "error"; error: string }
  | {
      /** The send was refused (`AGENT_CONFLICT`): takes back its `sendStart` — the user message it appended. */
      type: "conflict";
      userMessage: AgentChatMessage;
    }
  | {
      type: "openConversation";
      conversationId: string;
      messages: AgentChatMessage[];
    }
  | {
      /**
       * Shows a turn still running in the background again (its conversation
       * was reopened): the conversation as sent, then every event so far.
       */
      type: "attachTurn";
      conversationId: string;
      messages: AgentChatMessage[];
      events: readonly AgentStreamEvent[];
      stopping: boolean;
    }
  | { type: "stopRequested" }
  | {
      /** A send refused locally: the shown conversation's turn is still running. */
      type: "busy";
    }
  | {
      /** A background re-fetch of the already-open conversation (`refreshConversation`) — unlike `openConversation`, leaves `draftText`/`error`/`isLoading`/`progress` untouched. */
      type: "refreshMessages";
      conversationId: string;
      messages: AgentChatMessage[];
    }
  | { type: "reset" };

export function agentChatReducer(
  state: AgentChatState,
  action: AgentChatAction
): AgentChatState {
  switch (action.type) {
    case "sendStart":
      return {
        ...state,
        messages: [...state.messages, action.userMessage],
        isLoading: true,
        progress: [],
        error: null,
        draftText: "",
        conflict: false,
        stopping: false,
      };
    case "conversation":
      return { ...state, conversationId: action.conversationId };
    case "toolStart":
      return {
        ...state,
        progress: [
          ...state.progress,
          {
            callId: action.callId,
            name: action.name,
            done: false,
            failed: false,
          },
        ],
      };
    case "toolDone": {
      const index = state.progress.findIndex(
        p => p.callId === action.callId && !p.done
      );
      if (index < 0) return state;
      const next = [...state.progress];
      const current = next[index];
      if (!current) return state;
      next[index] = {
        ...current,
        done: true,
        failed: action.entry.resultPreview.startsWith("ERROR:"),
      };
      return { ...state, progress: next };
    }
    case "textDelta":
      return { ...state, draftText: state.draftText + action.delta };
    case "textDiscard":
      return { ...state, draftText: "" };
    case "result":
      return {
        ...state,
        messages: [...state.messages, action.assistantMessage],
        isLoading: false,
        progress: [],
        draftText: "",
        stopping: false,
      };
    case "error":
      return {
        ...state,
        isLoading: false,
        progress: [],
        error: action.error,
        draftText: "",
        stopping: false,
      };
    case "conflict":
      return {
        ...state,
        messages: state.messages.filter(m => m !== action.userMessage),
        isLoading: false,
        progress: [],
        draftText: "",
        conflict: true,
      };
    case "openConversation":
      return {
        ...state,
        conversationId: action.conversationId,
        messages: action.messages,
        error: null,
        draftText: "",
        conflict: false,
      };
    case "attachTurn":
      return action.events.reduce<AgentChatState>(
        (current, event) => {
          const next = streamEventAction(event);
          return next ? agentChatReducer(current, next) : current;
        },
        {
          ...initialAgentChatState,
          conversationId: action.conversationId,
          messages: action.messages,
          isLoading: true,
          stopping: action.stopping,
        }
      );
    case "stopRequested":
      return state.isLoading ? { ...state, stopping: true } : state;
    case "busy":
      return { ...state, conflict: true };
    case "refreshMessages":
      return {
        ...state,
        conversationId: action.conversationId,
        messages: action.messages,
      };
    case "reset":
      return initialAgentChatState;
    default:
      return state;
  }
}

/**
 * The reducer action one non-terminal stream event stands for, or `null`
 * (a ping; `result`/`error` are dispatched with more context by `send`).
 * Shared by a live turn and by replaying a background turn's events.
 */
export function streamEventAction(
  event: AgentStreamEvent
): AgentChatAction | null {
  switch (event.type) {
    case "conversation":
      return { type: "conversation", conversationId: event.conversationId };
    case "tool_start":
      return {
        type: "toolStart",
        callId: event.callId,
        name: event.name,
        input: event.input,
      };
    case "tool_done":
      return { type: "toolDone", callId: event.callId, entry: event.entry };
    case "text_delta":
      return { type: "textDelta", delta: event.delta };
    case "text_discard":
      return { type: "textDiscard" };
    default:
      return null;
  }
}

/** Mirrors the backend's `messages` cap (`ai-assistant-chat` spec: 1–40 items). */
export const AGENT_CHAT_MAX_MESSAGES = 40;

/**
 * Keeps at most `max` of the newest messages so a long-running conversation
 * (the backend loads nothing from `conversationId`, only what is sent)
 * never exceeds the request's cap. Always keeps the newest message; drops
 * one more from the front when the cut would otherwise start mid-turn on an
 * assistant message, so the kept slice always opens with a user turn.
 */
export function capAgentMessages<T extends { role: "user" | "assistant" }>(
  messages: readonly T[],
  max: number
): T[] {
  if (messages.length <= max) return [...messages];
  const tail = messages.slice(-max);
  return tail[0]?.role === "user" ? tail : tail.slice(1);
}

/** Per-view cap for the memory text appended to a request message (`ai-assistant-chat-panel` spec). */
export const AGENT_VIEW_MEMORY_MAX_CHARS = 8000;

/** Reads a view action's optional `memory` string type-safely. */
function getViewMemory(
  view: { component: string } & Record<string, unknown>
): string | undefined {
  const memory = view.memory;
  return typeof memory === "string" ? memory : undefined;
}

/**
 * The `content` to send for one message of the request history: the
 * message's own content plus every view action's `memory` (each cut to
 * `AGENT_VIEW_MEMORY_MAX_CHARS`), separated by a blank line. Not rendered
 * and not written back to state — only the outgoing copy changes.
 */
export function buildAgentRequestMessageContent(message: {
  content: string;
  uiActions?: AgentUiAction[];
}): string {
  const memories = (message.uiActions ?? []).flatMap(action => {
    if (action.type !== "view") return [];
    const memory = getViewMemory(action.view);
    return memory ? [memory.slice(0, AGENT_VIEW_MEMORY_MAX_CHARS)] : [];
  });
  return [message.content, ...memories].join("\n\n");
}

export type AgentHistoryMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachmentNames: string[];
  uiActions: AgentUiAction[];
  toolCalls?: AgentToolCall[];
  model?: string | null;
  costUsd?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
};

/** Shared by `openConversation` and `refreshConversation`: the history query's wire shape to this hook's own message shape. */
function mapHistoryMessages(
  messages: readonly AgentHistoryMessage[]
): AgentChatMessage[] {
  return messages.map(m => ({
    id: m.role === "assistant" ? m.id : undefined,
    role: m.role,
    content: m.content,
    attachmentNames: m.attachmentNames,
    uiActions: m.uiActions,
    toolCalls: m.toolCalls,
    model: m.model,
    costUsd: m.costUsd,
    promptTokens: m.promptTokens,
    completionTokens: m.completionTokens,
  }));
}

/** One `agentConversation(id)` message node (`AgentConversationHistoryQuery`) as this hook's own `AgentHistoryMessage`. */
function toHistoryMessage(
  node: Extract<
    NonNullable<AgentConversationHistoryQuery["agentConversation"]>,
    { __typename: "AgentConversation" }
  >["messages"][number]
): AgentHistoryMessage {
  return {
    id: node.id,
    role: node.role,
    content: node.content,
    attachmentNames: node.attachmentNames,
    uiActions: node.uiActions as AgentUiAction[],
    toolCalls: node.toolCalls as AgentToolCall[],
    model: node.model,
    costUsd: node.costUsd,
    promptTokens: node.promptTokens,
    completionTokens: node.completionTokens,
  };
}

export function useAgentChat({ resetKey }: { resetKey: string }) {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  // No event subscription: the language is read at send time (below), so a
  // switch applies to the very next turn without re-rendering this hook.
  const tolgee = useTolgee();
  const { t } = useTranslate(["Assistant"]);
  const refreshAfterAgentWrite = useRefreshAfterAgentWrite();
  const [ownState, dispatch] = React.useReducer(
    agentChatReducer,
    initialAgentChatState
  );

  /**
   * The `resetKey` the reducer's state belongs to. When `resetKey` changes
   * the reset is queued during render (React re-renders right away, before
   * committing) and, for the render in which it changed, the empty state is
   * returned instead of the previous identity's — a passive effect alone
   * would let `AgentPanel`'s remounted chat box take the old messages as
   * its initial ones.
   */
  const [stateResetKey, setStateResetKey] = React.useState(resetKey);
  if (stateResetKey !== resetKey) {
    setStateResetKey(resetKey);
    dispatch({ type: "reset" });
  }
  const state = stateResetKey === resetKey ? ownState : initialAgentChatState;

  /**
   * Bumped whenever `resetKey` changes (design D8), and also by `newChat`
   * and `openConversation` themselves, so switching conversations
   * invalidates whatever the conversation being replaced still had in
   * flight. `send`/`openConversation` capture the current value on entry
   * and check it again after every await; a call still in flight for the
   * previous selected user/organisation, or the previous conversation, sees
   * a mismatch and discards its result instead of dispatching into — or
   * navigating from — the panel that has since moved on.
   */
  const generationRef = React.useRef(0);

  /** Every turn of this identity, shown or running in the background (`agentBackgroundTurns.ts`). */
  const [backgroundTurns] = React.useState(() => new AgentBackgroundTurns());

  React.useEffect(() => {
    generationRef.current += 1;
    // The previous identity's turns stop being read and are forgotten —
    // no notice, marker or badge of theirs outlives the switch.
    backgroundTurns.clear();
  }, [resetKey, backgroundTurns]);

  /**
   * `stopAgentTurn`: the backend marks the conversation's running turn,
   * which then starts no further tool call or model request and ends
   * through its normal result (`stopReason` "stopped") — so the turn's own
   * stream, not this call, ends it here too. A failure is reported by the
   * app-wide error link; the turn then simply runs to its end.
   */
  const requestTurnStop = React.useCallback(
    (conversationId: string) => {
      apolloClient
        .mutate<StopAgentTurnMutation, StopAgentTurnMutationVariables>({
          mutation: StopAgentTurnDocument,
          variables: { conversationId },
        })
        .catch((err: unknown) => Sentry.captureException(err));
    },
    [apolloClient]
  );

  /**
   * Names a running turn's conversation (the send mutation's answer, or its
   * stream's `conversation` event — whichever comes first; the second is a
   * no-op): listed in the history at once, and a stop asked for before the
   * conversation was known goes out now.
   */
  const nameTurnConversation = React.useCallback(
    (key: string, conversationId: string) => {
      if (!backgroundTurns.nameConversation(key, conversationId)) return;
      if (backgroundTurns.get(key)?.stopRequested)
        requestTurnStop(conversationId);
    },
    [backgroundTurns, requestTurnStop]
  );

  /** Stops the shown turn (the composer's stop button); a no-op without one, or when already stopping. */
  const stop = React.useCallback(() => {
    const turn = backgroundTurns.attached();
    if (!turn || turn.stopRequested) return;
    backgroundTurns.requestStop(turn.key);
    dispatch({ type: "stopRequested" });
    if (turn.conversationId) requestTurnStop(turn.conversationId);
  }, [backgroundTurns, requestTurnStop]);

  /** `agentConversation(id)`, unwrapped and mapped to this hook's own history shape; throws on a refusal or an unknown/foreign id. */
  const fetchConversationHistory = React.useCallback(
    async (
      conversationId: string
    ): Promise<{ conversationId: string; messages: AgentHistoryMessage[] }> => {
      const { data } = await apolloClient.query<
        AgentConversationHistoryQuery,
        AgentConversationHistoryQueryVariables
      >({
        query: AgentConversationHistoryDocument,
        variables: { id: conversationId },
        fetchPolicy: "network-only",
      });
      const conversation = getDataOrNull(data.agentConversation ?? undefined);
      if (!conversation) throw new Error("This conversation is not available.");
      return {
        conversationId: conversation.conversationId,
        messages: conversation.messages.map(toHistoryMessage),
      };
    },
    [apolloClient]
  );

  /**
   * The answer of a turn whose stream was lost, read from the stored
   * conversation: polls its history (network-only) until a stored assistant
   * message follows the `sentCount` messages sent, for at most
   * `AGENT_RECOVERY_MAX_MS` — then fails like a stream that ended without an
   * answer. A failed poll is retried; the turn being settled or cleared
   * meanwhile ends it quietly (`send` checks `isActive` right after).
   */
  const recoverStoredAnswer = React.useCallback(
    async (
      key: string,
      conversationId: string,
      sentCount: number
    ): Promise<AgentChatResult> => {
      const deadline = Date.now() + AGENT_RECOVERY_MAX_MS;
      while (Date.now() < deadline) {
        await new Promise<void>(resolve =>
          setTimeout(resolve, AGENT_RECOVERY_POLL_MS)
        );
        if (!backgroundTurns.isActive(key))
          throw new AgentStreamAbortedError("Stopped reading the answer.");
        try {
          const { messages } = await fetchConversationHistory(conversationId);
          const answer = messages[messages.length - 1];
          if (
            answer &&
            answer.role === "assistant" &&
            messages.length > sentCount
          )
            return {
              messageId: answer.id,
              reply: answer.content,
              toolCalls: answer.toolCalls ?? [],
              uiActions: answer.uiActions,
              stopReason: null,
              model: answer.model ?? "",
              costUsd: answer.costUsd ?? null,
              promptTokens: answer.promptTokens ?? 0,
              completionTokens: answer.completionTokens ?? 0,
            };
        } catch {
          // Retried on the next poll, until the deadline.
        }
      }
      throw new AgentStreamError(
        "The connection ended before the answer arrived."
      );
    },
    [backgroundTurns, fetchConversationHistory]
  );

  const send = React.useCallback(
    async (
      content: string,
      options: {
        context: PageContext;
        attachments?: UploadedAgentAttachment[];
        confirm?: boolean;
        /** The id the sending box streams the answer under (`AgentBackgroundTurn.messageId`). */
        messageId?: string;
        /**
         * Called once the turn actually started — its stream's first event
         * (every started turn opens with its `conversation` event), before
         * that event is handed to `onEvent`. Never for a refusal: neither
         * the mutation's `AGENT_CONFLICT` nor a raced send's terminal
         * `AgentTurnUnavailableEvent`, which is always its stream's only event.
         */
        onAccepted?: () => void;
        /**
         * Every raw stream event, as it arrives — for `agentChatAdapter.js`
         * to translate into MUI X Chat's chunk protocol while this same
         * turn also updates the reducer above. Includes the terminal
         * `result`/`error`, which `readAgentMessageStream` does not hand to
         * its own `onEvent`. Only while the sending box still reads the
         * turn (`foreground`): not after a new chat or a switch to another
         * conversation, when the turn goes on in the background.
         */
        onEvent?: (event: AgentStreamEvent) => void;
      }
    ): Promise<AgentSendOutcome> => {
      const attachments = options.attachments ?? [];
      if (!content.trim() && attachments.length === 0) return "done";

      // The shown conversation's turn still runs — reopened from the
      // background, so the composer is not locked by a stream of its own.
      // Refused right here, exactly like the backend's `AGENT_CONFLICT`.
      const shownTurn = backgroundTurns.attached();
      if (shownTurn && shownTurn.conversationId === state.conversationId) {
        dispatch({ type: "busy" });
        return "conflict";
      }

      // An attachment-only message carries the default request to evaluate
      // the attachments (the same wording the backend substitutes), so the
      // copy kept here and replayed without attachments in later requests'
      // history is never empty.
      const messageContent = content.trim()
        ? content
        : attachments.length === 1
          ? t("Please evaluate the attached file.", { ns: "Assistant" })
          : t("Please evaluate the attached files.", { ns: "Assistant" });
      const userMessage: AgentChatMessage = {
        role: "user",
        content: messageContent,
        attachmentNames: attachments.map(a => a.name),
      };
      const priorMessages = [...state.messages, userMessage];
      const messagesToSend = capAgentMessages(
        priorMessages,
        AGENT_CHAT_MAX_MESSAGES
      );
      const conversationId = state.conversationId;
      // The UI language shown right now — read per send, never captured
      // earlier, so a mid-conversation switch applies to the next answer.
      const locale = tolgee.getLanguage();
      const turn = backgroundTurns.begin({
        messages: priorMessages,
        conversationId,
        title: provisionalConversationTitle(messageContent),
        messageId: options.messageId,
      });
      const { key } = turn;
      // Shown in the panel right now (fed to the reducer)…
      const isAttached = () => backgroundTurns.get(key)?.attached ?? false;
      // …and still read by the box that sent it (fed to its stream).
      const isForeground = () => backgroundTurns.get(key)?.foreground ?? false;
      dispatch({ type: "sendStart", userMessage });

      try {
        const { data: sendData } = await apolloClient.mutate<
          SendAgentMessageMutation,
          SendAgentMessageMutationVariables
        >({
          mutation: SendAgentMessageDocument,
          variables: {
            input: {
              ...(conversationId ? { conversationId } : {}),
              messages: messagesToSend.map((m, index) => ({
                role: m.role,
                content: buildAgentRequestMessageContent(m),
                ...(index === messagesToSend.length - 1 &&
                attachments.length > 0
                  ? {
                      attachments: attachments.map(a => ({
                        name: a.name,
                        mediaType: a.mediaType,
                        uploadKey: a.uploadKey,
                      })),
                    }
                  : {}),
              })),
              context: options.context,
              ...(options.confirm ? { confirm: true } : {}),
              ...(locale ? { locale } : {}),
            },
          },
          // Every failure is shown inside the panel (the inline conflict
          // notice, or the translated "could not answer" fallback) — never
          // as the global error link's raw-message toast.
          context: { noErrorNotification: true },
        });
        if (!sendData)
          throw new Error("The assistant did not accept the request.");
        // The selected user/organisation changed meanwhile (`clear()`).
        if (!backgroundTurns.isActive(key)) return "done";
        const { turnId, conversationId: sentConversationId } =
          sendData.sendAgentMessage;
        // Known right here, also for a brand-new chat (the backend creates
        // it before answering): listed in the history from the send on,
        // not only once the stream names it.
        nameTurnConversation(key, sentConversationId);
        if (isAttached())
          dispatch({
            type: "conversation",
            conversationId: sentConversationId,
          });

        const observable = apolloClient.subscribe<
          AgentMessageStreamSubscription,
          AgentMessageStreamSubscriptionVariables
        >({
          query: AgentMessageStreamDocument,
          variables: { turnId },
        });

        let started = false;
        const readStream = () =>
          readAgentMessageStream(
            observable,
            event => {
              if (!backgroundTurns.isActive(key)) return;
              const foreground = isForeground();
              const isFirst = !started;
              if (!started) {
                started = true;
                if (foreground) options.onAccepted?.();
              }
              if (event.type === "conversation") {
                const alreadyNamed =
                  backgroundTurns.get(key)?.conversationId ===
                  event.conversationId;
                nameTurnConversation(key, event.conversationId);
                // The turn holds the conversation lock only from here: a stop
                // sent earlier found nothing to stop, so ask again — unless this
                // event named the conversation itself (that already sent it).
                if (
                  isFirst &&
                  alreadyNamed &&
                  backgroundTurns.get(key)?.stopRequested
                )
                  requestTurnStop(event.conversationId);
              }
              backgroundTurns.record(key, event);
              if (foreground) options.onEvent?.(event);
              if (isAttached()) {
                const action = streamEventAction(event);
                if (action) dispatch(action);
              }
            },
            turn.signal
          );
        const result = await readStream().catch(
          async (streamErr: unknown): Promise<AgentChatResult> => {
            // The connection dropped mid-turn and graphql-sse re-subscribed:
            // the server already handed the turn out and keeps running it,
            // storing its answer. Before the turn started here it is a real
            // refusal; after, the answer is read back from the history.
            if (
              !started ||
              !(streamErr instanceof AgentStreamError) ||
              streamErr.message !== AGENT_TURN_TAKEN_REASON
            )
              throw streamErr;
            return recoverStoredAnswer(
              key,
              backgroundTurns.get(key)?.conversationId ?? sentConversationId,
              priorMessages.length
            );
          }
        );
        if (!backgroundTurns.isActive(key)) return "done";
        const attached = isAttached();
        const foreground = isForeground();
        backgroundTurns.settle(key, { status: "finished", result });
        if (foreground) options.onEvent?.({ type: "result", ...result });

        if (attached)
          dispatch({
            type: "result",
            assistantMessage: {
              id: result.messageId ?? undefined,
              role: "assistant",
              content: result.reply,
              toolCalls: result.toolCalls,
              uiActions: result.uiActions,
              model: result.model,
              costUsd: result.costUsd,
              promptTokens: result.promptTokens,
              completionTokens: result.completionTokens,
              tiers: result.tiers,
              escalatedAtIteration: result.escalatedAtIteration,
            },
          });

        // Does not block showing the reply: an open view (e.g. the template
        // editor) is let to catch up in the background — also after an
        // answer that finished unshown, whose writes happened all the same.
        void refreshAfterAgentWrite(result.toolCalls).catch(
          (refreshErr: unknown) => Sentry.captureException(refreshErr)
        );

        // Only the shown answer navigates: one finishing in the background
        // never pulls the user away from what they are looking at.
        const navigation = attached
          ? [...result.uiActions]
              .reverse()
              .find(
                (a): a is Extract<AgentUiAction, { type: "navigate" }> =>
                  a.type === "navigate"
              )
          : undefined;
        if (navigation) navigate(navigation.path);
        return "done";
      } catch (err) {
        // Settled, or cleared by an identity switch (`AgentStreamAbortedError`).
        if (!backgroundTurns.isActive(key)) return "done";
        const attached = isAttached();
        const foreground = isForeground();
        if (isAgentConflictError(err)) {
          // Expected while an earlier turn still runs: not an error, no
          // Sentry report, no retry — the user sends again once it is done.
          backgroundTurns.discard(key);
          if (attached) dispatch({ type: "conflict", userMessage });
          return foreground ? "conflict" : "done";
        }
        backgroundTurns.settle(key, { status: "failed" });
        Sentry.captureException(err);
        const message = err instanceof Error ? err.message : String(err);
        if (foreground) options.onEvent?.({ type: "error", error: message });
        if (attached) dispatch({ type: "error", error: message });
        return "done";
      }
    },
    [
      apolloClient,
      tolgee,
      t,
      navigate,
      state.messages,
      state.conversationId,
      recoverStoredAnswer,
      refreshAfterAgentWrite,
      backgroundTurns,
      nameTurnConversation,
    ]
  );

  const openConversation = React.useCallback(
    async (conversationId: string): Promise<AgentChatMessage[] | undefined> => {
      // Bumped before capturing this call's own generation: any `send` or
      // `openConversation` already in flight for the conversation being
      // replaced is invalidated right away, instead of only once this call
      // itself resolves.
      generationRef.current += 1;
      const generation = generationRef.current;
      // The turn shown until now keeps running in the background.
      backgroundTurns.detachAll();
      backgroundTurns.markOpened(conversationId);
      const running = backgroundTurns.attach(conversationId);
      if (running) {
        // Still being answered: shown from the running turn itself (the
        // stored conversation lacks the answer being written), live.
        const messages = [...running.messages];
        dispatch({
          type: "attachTurn",
          conversationId,
          messages,
          events: running.events,
          stopping: running.stopRequested,
        });
        return messages;
      }
      try {
        const data = await fetchConversationHistory(conversationId);
        // The selected user/organisation changed while this load was in
        // flight — the panel has since been reset for a new identity, so
        // this stored conversation must not be dispatched into it.
        if (generationRef.current !== generation) return undefined;
        const messages = mapHistoryMessages(data.messages);
        dispatch({
          type: "openConversation",
          conversationId: data.conversationId,
          messages,
        });
        // Read by `agentChatAdapter.js` (`listMessages`) to seed MUI X
        // Chat's own store without a second fetch or a stale-closure race
        // on this hook's state right after the dispatch above.
        return messages;
      } catch (err) {
        if (generationRef.current !== generation) return undefined;
        Sentry.captureException(err);
        dispatch({
          type: "error",
          error: err instanceof Error ? err.message : String(err),
        });
        return undefined;
      }
    },
    [fetchConversationHistory, backgroundTurns]
  );

  /**
   * Re-fetches the currently open conversation in the background (e.g. to
   * pick up a stored answer's replay cost, booked server-side after the
   * fact) without the disruption of `openConversation`'s initial-load
   * semantics: a no-op without an open conversation, and — unlike
   * `openConversation` — leaves `draftText`/`error`/`isLoading`/`progress`
   * untouched so it never clobbers an in-flight turn.
   */
  const refreshConversation = React.useCallback(async (): Promise<void> => {
    const conversationId = state.conversationId;
    if (!conversationId) return;
    const generation = generationRef.current;
    try {
      const data = await fetchConversationHistory(conversationId);
      // The selected user/organisation changed while this refresh was in
      // flight — the panel has since been reset for a new identity.
      if (generationRef.current !== generation) return;
      dispatch({
        type: "refreshMessages",
        conversationId: data.conversationId,
        messages: mapHistoryMessages(data.messages),
      });
    } catch (err) {
      if (generationRef.current !== generation) return;
      // Best-effort background refresh: report to Sentry, but keep the
      // conversation showing as-is rather than surfacing an error banner
      // for a refresh nothing user-visible depends on.
      Sentry.captureException(err);
    }
  }, [fetchConversationHistory, state.conversationId]);

  const newChat = React.useCallback(() => {
    // Invalidates a `send`/`openConversation` still in flight for the
    // conversation being replaced, the same way the `resetKey` effect does
    // for a selected user/organisation switch — a late resolution must not
    // dispatch into the freshly reset state.
    generationRef.current += 1;
    // A turn still running keeps going in the background.
    backgroundTurns.detachAll();
    dispatch({ type: "reset" });
  }, [backgroundTurns]);

  /**
   * A conversation was deleted from the history: its running turn is
   * stopped (backend and registry) and forgotten, and — when it is the chat
   * shown — the panel starts empty, so the next message never goes into
   * the deleted conversation.
   */
  const forgetConversation = React.useCallback(
    (conversationId: string) => {
      if (backgroundTurns.isRunning(conversationId))
        requestTurnStop(conversationId);
      const shown =
        (backgroundTurns.attached()?.conversationId ?? state.conversationId) ===
        conversationId;
      backgroundTurns.forgetConversation(conversationId);
      if (!shown) return;
      generationRef.current += 1;
      backgroundTurns.detachAll();
      dispatch({ type: "reset" });
    },
    [backgroundTurns, requestTurnStop, state.conversationId]
  );

  return {
    ...state,
    backgroundTurns,
    send,
    stop,
    openConversation,
    refreshConversation,
    newChat,
    forgetConversation,
  };
}
